import { useDeferredValue, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { History, Loader2, PackageSearch, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Product {
  id: number;
  name: string;
  sku: string;
}

interface Branch {
  id: number;
  name: string;
}

interface StockMovement {
  id: number;
  productId: number;
  productName: string;
  productSku: string;
  branchId: number | null;
  branchName: string;
  type: string;
  quantity: number;
  stockBefore: number;
  stockAfter: number;
  reason: string | null;
  notes: string | null;
  referenceType: string;
  referenceId: string;
  actorName: string;
  actorRole: string;
  createdAt: string;
}
interface StockMovementResponse {
  rows: StockMovement[];
  page: number;
  pageSize: number;
  total: number;
}

const MOVEMENT_LABELS: Record<string, string> = {
  opening_balance: "Opening balance",
  sale: "Sale",
  sale_reversal: "Sale reversal",
  purchase_receipt: "Purchase receipt",
  purchase_return: "Supplier return",
  customer_return: "Customer return",
  online_order: "Online order",
  adjustment: "Adjustment",
  transfer_out: "Transfer sent",
  transfer_in: "Transfer received",
  transfer_cancelled: "Transfer cancelled",
};

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, { credentials: "include" });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? "Failed to load stock history");
  }
  return response.json();
}

export default function StockMovements() {
  const [productId, setProductId] = useState("all");
  const [branchId, setBranchId] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const deferredSearch = useDeferredValue(search);
  useEffect(() => setPage(1), [productId, branchId, deferredSearch]);

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ["products", "movement-filter"],
    queryFn: () => getJson("/api/products"),
  });
  const { data: branches = [] } = useQuery<Branch[]>({
    queryKey: ["branches"],
    queryFn: async () => {
      const response = await fetch(`${BASE}/api/branches`, { credentials: "include" });
      return response.ok ? response.json() : [];
    },
  });

  const params = new URLSearchParams();
  if (productId !== "all") params.set("productId", productId);
  if (branchId !== "all") params.set("branchId", branchId);
  if (deferredSearch.trim()) params.set("search", deferredSearch.trim());
  params.set("page", String(page));
  params.set("pageSize", "200");
  const query = params.toString();

  const { data, isLoading, error } = useQuery<StockMovementResponse>({
    queryKey: ["stock-movements", productId, branchId, deferredSearch, page],
    queryFn: () => getJson(`/api/stock-movements?${query}`),
  });
  const movements = data?.rows ?? [];
  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / 200));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <History className="w-7 h-7 text-primary" />
          Stock Movement History
        </h1>
        <p className="text-muted-foreground mt-1">
          Review every stock change, including sales, returns, online orders, receipts, adjustments, and transfers.
        </p>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search product, SKU, staff or details..."
              className="pl-9"
              aria-label="Search stock movement history"
            />
          </div>
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger className="lg:w-[300px]"><SelectValue placeholder="All products" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              {products.map(product => (
                <SelectItem key={product.id} value={String(product.id)}>
                  {product.name} ({product.sku})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="lg:w-[240px]"><SelectValue placeholder="All locations" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {branches.map(branch => (
                <SelectItem key={branch.id} value={String(branch.id)}>{branch.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <div className="border rounded-xl overflow-x-auto bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Movement</TableHead>
              <TableHead>Staff</TableHead>
              <TableHead className="text-right">Change</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="h-40 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={8} className="h-40 text-center text-destructive">
                  {error instanceof Error ? error.message : "Failed to load stock history"}
                </TableCell>
              </TableRow>
            ) : movements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-48 text-center">
                  <PackageSearch className="w-9 h-9 mx-auto mb-2 text-muted-foreground" />
                  <p className="font-medium">No stock movements found</p>
                  <p className="text-sm text-muted-foreground">Try a different search, product, or location filter.</p>
                </TableCell>
              </TableRow>
            ) : movements.map(movement => (
              <TableRow key={movement.id}>
                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                  {format(new Date(movement.createdAt), "dd MMM yyyy, h:mm a")}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{movement.productName}</div>
                  <div className="text-xs text-muted-foreground">{movement.productSku}</div>
                </TableCell>
                <TableCell>{movement.branchName}</TableCell>
                <TableCell>
                  <Badge variant="outline">{MOVEMENT_LABELS[movement.type] ?? movement.type}</Badge>
                </TableCell>
                <TableCell>
                  <div className="font-medium whitespace-nowrap">{movement.actorName}</div>
                  {movement.actorRole !== "system" && (
                    <div className="text-xs text-muted-foreground capitalize">{movement.actorRole}</div>
                  )}
                </TableCell>
                <TableCell className={`text-right font-semibold ${movement.quantity > 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {movement.quantity > 0 ? "+" : ""}{movement.quantity}
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-xs text-muted-foreground">{movement.stockBefore} → </span>
                  <span className="font-medium">{movement.stockAfter}</span>
                </TableCell>
                <TableCell className="max-w-[260px]">
                  <div className="text-sm">{movement.reason ?? "—"}</div>
                  <div className="text-xs text-muted-foreground">
                    Ref: {movement.referenceType.replaceAll("_", " ")} #{movement.referenceId}
                  </div>
                  {movement.notes && <div className="text-xs text-muted-foreground truncate">{movement.notes}</div>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data && data.total > 200 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {(page - 1) * 200 + 1}–{Math.min(page * 200, data.total)} of {data.total.toLocaleString()} movements</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Previous</Button>
            <span className="px-2 py-1">Page {page} of {pageCount}</span>
            <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage(value => value + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}